/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.1
 */
import { Fragment, useCallback } from 'react';

import { EllipseText, LinkEllipseBlock } from '@components/common';
import { formatNumberToPhone } from '@components/common/PhoneMask/formatNumberToPhone';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import type { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import type { ICommonTranslation, IPageDashboardTranslation } from '@modules/internationalization/types';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';
import { formatBalance } from '@utils/formatText';

import {
    WidgetSubscriberMaxCostsListItemFioStyled,
    WidgetSubscriberMaxCostsListItemLeftStyled,
    WidgetSubscriberMaxCostsListItemRightStyled,
    WidgetSubscriberMaxCostsListItemStyled,
} from '../styled/widgetSubscriberMaxCostsStyled';
import type { ISubscribersMaxCost } from '../widgetSubscriberMaxCostsType';

export interface IWidgetSubscriberMaxCostsListProps {
    subscribers: ISubscribersMaxCost[];
    translation: IPageDashboardTranslation & ICommonTranslation;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

const WidgetSubscriberMaxCostsList = ({
    subscribers,
    translation,
    pushGtmAnalytic,
}: IWidgetSubscriberMaxCostsListProps) => {

    const onLinkClick = useCallback((): void => {
        pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMaxCosts.onClickSubscriberLinkWidgetSubscriberMaxCosts('Абонент'),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic]);

    return (
        <Fragment>
            {
                subscribers.map((item: ISubscribersMaxCost) =>
                    <WidgetSubscriberMaxCostsListItemStyled key={item.subsId}>
                        <WidgetSubscriberMaxCostsListItemLeftStyled>
                            <LinkEllipseBlock
                                className="subscriber-link"
                                dataName="redirectToMobileFinance"
                                title={formatNumberToPhone(item.phone)}
                                url={RoutePath.subscriberIdFinances.replace(CLIENT_ID, item.subsId?.toString())}
                                onClick={() => onLinkClick()}
                            >
                                {formatNumberToPhone(item.phone)}
                            </LinkEllipseBlock>
                            {
                                !!item.fio &&
                                <EllipseText
                                    title={item.fio}
                                >
                                    <WidgetSubscriberMaxCostsListItemFioStyled>
                                        {item.fio}
                                    </WidgetSubscriberMaxCostsListItemFioStyled>
                                </EllipseText>
                            }
                        </WidgetSubscriberMaxCostsListItemLeftStyled>
                        <WidgetSubscriberMaxCostsListItemRightStyled>
                            {formatBalance(item.spent)}&nbsp;{translation.currency}
                        </WidgetSubscriberMaxCostsListItemRightStyled>
                    </WidgetSubscriberMaxCostsListItemStyled>,
                )
            }
        </Fragment>
    );
};

export default WidgetSubscriberMaxCostsList;
